import { type ReactNode, useState } from "react";

import { IconCaretSmallDown } from "@btnet/blueprint-icons";
import { BTText } from "@btnet/core-components/BTText";
import { cn } from "@btnet/utilities-core";

import "./BillCollapsibleSection.less";

interface IBillCollapsibleSectionProps {
    /**
     * Section heading. Omit for a headerless card (only valid with
     * `collapsible={false}`, since a collapsible section needs a header to toggle).
     */
    title?: string;
    /** Inline content rendered next to the title (e.g. a status badge). */
    titleExtra?: ReactNode;
    children: ReactNode;
    /** @default true */
    defaultOpen?: boolean;
    /**
     * When false the section is locked open: no caret, no toggle, content
     * always visible. Use for primary sections where hiding the content
     * would defeat the purpose of the page.
     * @default true
     */
    collapsible?: boolean;
    testid?: string;
    className?: string;
}

/**
 * Collapsible section card with smooth height animation, an optional inline
 * status slot, and an optional collapsed-state summary.
 *
 * Self-contained — no third-party Collapse component, no class overrides.
 *
 * The expand/collapse animation uses the CSS Grid `grid-template-rows:
 * 0fr → 1fr` trick so the content animates to/from its natural height —
 * which `transition: height auto` cannot do. The inner wrapper has
 * `overflow: hidden` so children clip while collapsing. Respects
 * `prefers-reduced-motion`.
 *
 * Accessibility:
 * - `<button aria-expanded>` for the trigger so screen readers announce state
 * - `<div role="region" aria-label={title}>` for the panel so it's discoverable
 *   via region navigation
 *
 * Uncontrolled by design — collapse state lives inside the component.
 * Pass `defaultOpen` for the starting state.
 */
export const BillCollapsibleSection = ({
    title,
    titleExtra,
    children,
    defaultOpen = true,
    collapsible = true,
    testid,
    className,
}: IBillCollapsibleSectionProps) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const toggle = () => setIsOpen((prev) => !prev);

    const headerInner = (
        <>
            <div className="BillCollapsibleSection-headerRow">
                <span className="BillCollapsibleSection-titleGroup">
                    <BTText size="distinct-lg">{title}</BTText>
                    {titleExtra && (
                        <span className="BillCollapsibleSection-titleExtra">{titleExtra}</span>
                    )}
                </span>
                {collapsible && (
                    <span className="BillCollapsibleSection-caret" aria-hidden="true">
                        <IconCaretSmallDown />
                    </span>
                )}
            </div>
        </>
    );

    return (
        <div
            data-testid={testid}
            className={cn("BillCollapsibleSection", className, {
                "BillCollapsibleSection--open": isOpen,
                "BillCollapsibleSection--static": !collapsible,
            })}
        >
            {collapsible ? (
                // Native button is preferred over BTButton here: BTButton's
                // tertiary styling (padding, hover bg) competes with the
                // section header layout. The native button is fully styled
                // by the LESS file and has `type="button"` so it never
                // submits an enclosing form.
                // eslint-disable-next-line react/forbid-elements
                <button
                    type="button"
                    aria-expanded={isOpen}
                    className="BillCollapsibleSection-header"
                    onClick={toggle}
                    data-testid={testid ? `${testid}-toggle` : undefined}
                >
                    {headerInner}
                </button>
            ) : (
                // A headerless static card renders no header row at all.
                title && <div className="BillCollapsibleSection-header">{headerInner}</div>
            )}
            <div
                role="region"
                aria-label={title}
                aria-hidden={collapsible && !isOpen}
                className="BillCollapsibleSection-content"
            >
                <div className="BillCollapsibleSection-content-inner">
                    <div className="BillCollapsibleSection-content-padding">{children}</div>
                </div>
            </div>
        </div>
    );
};
